import * as React from "react";
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router";
import MultiStepForm from "../components/MultiStepForm";
import { privat_contact_form, companyForm } from "../data/data";
import {
  Table,
  TableBody,
  TableCell,
  TableCellLayout,
  TableColumnDefinition,
  TableColumnSizingOptions,
  TableHeader,
  TableHeaderCell,
  TableRow,
  createTableColumn,
  useTableColumnSizing_unstable,
  useTableFeatures,
  Checkbox,
  Button,
  makeStyles,
  shorthands,
  RatingDisplay,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogOpenChangeData,
  DialogOpenChangeEvent,
  Popover,
  PopoverSurface,
  PopoverTrigger,
  Tooltip,
  tokens,
  mergeClasses,
} from "@fluentui/react-components";
import {
  EditRegular,
  DeleteRegular,
  Building32Filled,
  Person32Filled,
  Call20Regular,
  Mail20Regular,
  Filter20Regular,
  Dismiss20Regular,
} from "@fluentui/react-icons";
import InfiniteScroll from "react-infinite-scroll-component";
import { ContactDetailsFormValues } from "../types/Contact";
import { CommandBar } from "../components/CommandBar";
import { ContactScopeToggle, ContactScope, ContactViewMode } from "../components/contacts/ContactScopeToggle";
import { KundenverbindungList } from "../components/contacts/KundenverbindungList";
import { useAuth } from "../context/AuthContext";
import ContactMultiStepForm from "../components/ContactMultiStepForm";
import ContactMultiStepFormCompany from "../components/ContactMultiStepFormCompany";
import CreateContact from "../components/CreateContact";
import { ContactModesDialog } from "../components/ContactModesDialog"; // Import
import { OpenEventsIndicator, type EventTypeKey } from "../components/OpenEventsIndicator";
import { OffeneVorgaengeDialog } from "../components/OffeneVorgaengeDialog";
// Open Vorgaenge (incl. linked Todos) come from the per-row `openEventTypes`
// annotation ('V'); clicking the indicator opens the OffeneVorgaengeDialog.
import { ColumnFilter } from "../components/ColumnFilter";
import { apiUrl } from "../utils/api";
import { listAppUsers } from "../services/userService";
import { apiFetch } from "../utils/apiFetch";
import { fetchTeamsLite } from "../utils/teamsLite";
import { mapContactRow } from "../utils/contactRowMapper";
import { contactTypeLabel, contactTypeValue } from "../utils/contactTypeLabel";
import { useDebouncedValue } from "../utils/useDebouncedValue";
import {
  VERSTORBEN_ZEICHEN,
  istVerstorben,
  verstorbenTitel,
} from "../utils/contactDisplay";
import { useResponsive } from "../hooks/useResponsive";
import {
  CONTACT_TABLE_COLUMN_ORDER_SCOPE,
  fetchUserPreference,
  getCurrentUserPreferenceKey,
  saveUserPreference,
} from "../utils/userPreferences";
import { hinweisen } from "../components/redesign/bestaetigen";

const useStyles = makeStyles({
  stripedRow: {
    backgroundColor: "#f2f2f2",
  },
  mobileCards: {
    display: "grid",
    gap: "12px",
    padding: "10px",
  },
  contactCard: {
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusLarge,
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow8,
    padding: "0",
    display: "grid",
    gap: "0",
    overflow: "hidden",
  },
  cardTopBand: {
    height: "7px",
    background: "linear-gradient(90deg, #361836 0%, #6A2C91 70%, #FFA714 100%)",
  },
  cardContent: {
    display: "grid",
    gap: "10px",
    padding: "12px",
  },
  cardHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: "8px",
  },
  cardIdentity: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    minWidth: 0,
    flex: 1,
  },
  cardAvatar: {
    width: "38px",
    height: "38px",
    borderRadius: tokens.borderRadiusCircular,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    color: "white",
    flexShrink: 0,
  },
  cardHeaderMain: {
    display: "grid",
    gap: "4px",
    minWidth: 0,
    flex: 1,
  },
  cardTypeBadge: {
    display: "inline-flex",
    alignItems: "center",
    width: "fit-content",
    ...shorthands.borderRadius(tokens.borderRadiusCircular),
    ...shorthands.padding("2px", "8px"),
    backgroundColor: "#f4ecfb",
    color: "#6A2C91",
    fontSize: "12px",
    fontWeight: 600,
  },
  cardTitle: {
    fontWeight: 600,
    fontSize: "16px",
    lineHeight: "1.2",
    color: tokens.colorNeutralForeground1,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  cardSubTitle: {
    color: tokens.colorNeutralForeground2,
    fontSize: "12px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  cardBody: {
    display: "grid",
    gap: "7px",
    ...shorthands.padding("8px", "10px"),
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    backgroundColor: "#fbf8fe",
  },
  cardRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "8px",
  },
  cardLabel: {
    color: tokens.colorNeutralForeground3,
    fontSize: "12px",
  },
  cardValue: {
    fontSize: "13px",
    textAlign: "right",
    maxWidth: "60%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    color: tokens.colorNeutralForeground1,
  },
  cardActions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "8px",
    marginTop: "6px",
    ...shorthands.padding("2px", "2px", "0", "2px"),
  },
  statusChip: {
    display: "inline-flex",
    alignItems: "center",
    ...shorthands.borderRadius(tokens.borderRadiusCircular),
    ...shorthands.padding("2px", "8px"),
    fontSize: "11px",
    fontWeight: 600,
    backgroundColor: "#efe7f8",
    color: "#5f2685",
  },
  cellEllipsis: {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  stickyHeader: {
    position: "sticky",
    top: 0,
    zIndex: 10,
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow4,
  },
  draggableHeader: {
    position: "relative",
    cursor: "grab",
    userSelect: "none",
  },
  draggingHeader: {
    opacity: 0.55,
  },
  dropIndicatorBefore: {
    boxShadow: "inset 3px 0 0 #6c8cff",
  },
  dropIndicatorAfter: {
    boxShadow: "inset -3px 0 0 #6c8cff",
  },
  // Im mobilen Filter-Sheet das ColumnFilter-Trigger-Icon dauerhaft anzeigen
  // (auf Touch-Geräten gibt es keinen Hover, der es sonst einblendet).
  mobileFilterList: {
    "& .filter-icon": {
      visibility: "visible",
    },
  },
});

const PAGE_SIZE = 30;
// K-Relevanz / K-Wert wurden vorübergehend ausgeblendet (siehe
// apps/api/docs/business-logic/contact-table-columns.md). Die zugrundeliegenden
// Mapper-Felder (relevanz/value/phase) bleiben erhalten → Re-Add = Spalten-Defs
// wieder einfügen. Persistierte 10-Spalten-Order verwirft sich dann via
// isValidColumnOrder (Längen-Mismatch) selbst auf den neuen Default.
const REORDERABLE_COLUMN_IDS = [
  "Typ",
  "Name",
  "Ort",
  "Straße",
  "Telefon",
  "Kontaktstatus",
  "Kontaktverantwortlicher",
  "Vorgange",
] as const;

type ReorderableColumnId = (typeof REORDERABLE_COLUMN_IDS)[number];

const isValidColumnOrder = (value: unknown): value is ReorderableColumnId[] =>
  Array.isArray(value) &&
  value.length === REORDERABLE_COLUMN_IDS.length &&
  REORDERABLE_COLUMN_IDS.every((id) => value.includes(id as ReorderableColumnId));

const sanitizeColumnOrder = (value: unknown): ReorderableColumnId[] =>
  isValidColumnOrder(value) ? value : [...REORDERABLE_COLUMN_IDS];

const getContactTableColumnOrderStorageKey = (userKey: string | null) =>
  userKey
    ? `app-settings.contact-table.column-order:${userKey}`
    : "app-settings.contact-table.column-order";

const ContactTable: React.FC = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const [kontakte, setKontakte] = useState<ContactDetailsFormValues[]>([]);
  const [search, setSearch] = useState("");
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [modalOpen, setModalOpen] = useState(false);
  const [dialogState, setDialogState] = useState<{
    open: boolean;
    mode: "phone" | "email";
    contact: ContactDetailsFormValues | null;
  }>({ open: false, mode: "phone", contact: null });
  // Overview dialog for a contact's open Vorgänge + Todos (opened from the
  // 'V' indicator in the Vorgänge column / mobile card).
  const [vorgaengeDialog, setVorgaengeDialog] = useState<{
    open: boolean;
    contact: ContactDetailsFormValues | null;
  }>({ open: false, contact: null });

  // `openEventTypes` (the 'V' letter for open Vorgaenge) comes pre-computed
  // on every row from the backend. This deletes the multi-MB
  // `getAllVorgaenge()` fan-out that previously downloaded the entire
  // Vorgang table just to colour one badge per row.

  // Phone (≤768) → Karten-Liste; Tablet/Desktop → Tabelle (auch im
  // Tablet-Hochformat, dort mit Rotations-Hinweis). Siehe hooks/useResponsive.
  const { isPhone, isTablet, isPortrait } = useResponsive();
  const isMobileView = isPhone;
  // Mobiles Filter-Sheet (auf dem Phone gibt es keine Tabellen-Spaltenköpfe,
  // daher werden die Filter über einen Button in einem Dialog angeboten).
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);
  const [rotateHintDismissed, setRotateHintDismissed] = useState(false);
  const [columnOrder, setColumnOrder] = useState<ReorderableColumnId[]>([
    ...REORDERABLE_COLUMN_IDS,
  ]);
  const [userPreferenceKey, setUserPreferenceKey] = useState<string | null>(null);
  const [isColumnOrderHydrated, setIsColumnOrderHydrated] = useState(false);
  const [draggedColumnId, setDraggedColumnId] = useState<ReorderableColumnId | null>(null);
  const [dropTarget, setDropTarget] = useState<{
    columnId: ReorderableColumnId;
    position: "before" | "after";
  } | null>(null);
  const columnOrderSaveTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    let mounted = true;

    const loadColumnOrderPreference = async () => {
      const resolvedUserKey = await getCurrentUserPreferenceKey();
      if (!mounted) return;

      setUserPreferenceKey(resolvedUserKey);

      const storageKey = getContactTableColumnOrderStorageKey(resolvedUserKey);
      const readLocalFallback = () => {
        if (typeof window === "undefined") return [...REORDERABLE_COLUMN_IDS];
        try {
          const raw = window.localStorage.getItem(storageKey);
          return sanitizeColumnOrder(raw ? JSON.parse(raw) : null);
        } catch {
          return [...REORDERABLE_COLUMN_IDS];
        }
      };

      try {
        if (resolvedUserKey) {
          const serverValue = await fetchUserPreference<ReorderableColumnId[]>(
            resolvedUserKey,
            CONTACT_TABLE_COLUMN_ORDER_SCOPE,
          );
          if (!mounted) return;

          if (isValidColumnOrder(serverValue)) {
            setColumnOrder(serverValue);
            if (typeof window !== "undefined") {
              window.localStorage.setItem(storageKey, JSON.stringify(serverValue));
            }
            setIsColumnOrderHydrated(true);
            return;
          }
        }
      } catch (error) {
        console.error("Failed to load contact table column order preference", error);
      }

      if (!mounted) return;
      setColumnOrder(readLocalFallback());
      setIsColumnOrderHydrated(true);
    };

    loadColumnOrderPreference();

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (!isColumnOrderHydrated || typeof window === "undefined") return;

    const storageKey = getContactTableColumnOrderStorageKey(userPreferenceKey);
    window.localStorage.setItem(storageKey, JSON.stringify(columnOrder));

    if (columnOrderSaveTimeoutRef.current != null) {
      window.clearTimeout(columnOrderSaveTimeoutRef.current);
    }

    if (!userPreferenceKey) return;

    columnOrderSaveTimeoutRef.current = window.setTimeout(() => {
      saveUserPreference(
        userPreferenceKey,
        CONTACT_TABLE_COLUMN_ORDER_SCOPE,
        columnOrder,
      ).catch((error) => {
        console.error("Failed to save contact table column order preference", error);
      });
    }, 250);

    return () => {
      if (columnOrderSaveTimeoutRef.current != null) {
        window.clearTimeout(columnOrderSaveTimeoutRef.current);
        columnOrderSaveTimeoutRef.current = null;
      }
    };
  }, [columnOrder, isColumnOrderHydrated, userPreferenceKey]);



  // Hilfsfunktion: deduplicate by kontaktId
  const dedupeById = (arr: ContactDetailsFormValues[]) => {
    const m = new Map<number, ContactDetailsFormValues>();
    for (const it of arr) {
      if (it?.kontaktId !== undefined) m.set(it.kontaktId, it);
    }
    return Array.from(m.values());
  };

  const [filters, setFilters] = useState<Record<string, any>>({});
  const [scope, setScope] = useState<ContactScope>("all");
  const [viewMode, setViewMode] = useState<ContactViewMode>("contacts");
  const { isAdmin, isGfgl, canTempDeleteContacts } = useAuth();
  // After BE 3 the scope contract is canonical: send it straight through.
  // The old JS post-filter on `boUser.userId in teammates` is gone — BE 3's
  // `scope=team` now means "TEAM rows of caller's teams" exactly.
  // Deleting moves a contact to the Papierkorb (soft-delete): admin / gfgl plus
  // die temporäre app-weite Lösch-Allowlist (CLL/SAH). Das Backend erzwingt es
  // pro Zeile (RbacService); der Toolbar-Button ist für alle anderen versteckt.
  const canDelete = React.useMemo(
    () => isAdmin || isGfgl || canTempDeleteContacts,
    [isAdmin, isGfgl, canTempDeleteContacts],
  );

  // Kontakte vom Backend laden (mit Pagination und Suche). Debounced so
  // typing doesn't fire one request per keystroke; aborted on
  // dep-change/unmount so the last response always wins.
  const debouncedSearch = useDebouncedValue(search, 300);
  useEffect(() => {
    const controller = new AbortController();
    setKontakte([]);
    setOffset(0);

    (async () => {
      try {
        const { data: raw, total } = await apiFetch<any>("contact", {
          signal: controller.signal,
          params: {
            limit: PAGE_SIZE,
            offset: 0,
            search: debouncedSearch,
            filters: JSON.stringify(filters),
            scope,
            // BE 2: empty `?include=` triggers the slim payload shape (no
            // `relationsSrc`, no nested communications). All list-row cells
            // are satisfied by the default include set plus `openEventTypes`.
            include: '',
          },
        });
        if (controller.signal.aborted) return;
        const data = Array.isArray(raw) ? raw : [];
        const mapped = Array.isArray(data) ? data.map(mapContactRow) : [];

        const newKontakte = dedupeById(mapped);
        const newOffset = mapped.length;
        // Deterministic now that BE 1 exposes X-Total-Count. Fallback to
        // "page wasn't empty" only when the header is absent (older
        // backends; should not happen post-BE-1).
        const newHasMore =
          total !== null ? newOffset < total : mapped.length > 0;

        setKontakte(newKontakte);
        setHasMore(newHasMore);
        setOffset(newOffset);
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        // Surface real errors; abort errors are expected on dep-change.
        console.error("Fehler beim Laden der Kontakte", err);
      }
    })();

    return () => controller.abort();
  }, [debouncedSearch, filters, scope]);

  // Infinite Scroll: Nachladen (immer mit aktuellem Suchbegriff).
  // FE B will switch the hasMore predicate to `newOffset < total` once the
  // X-Total-Count header is consumed; for now we keep the "page wasn't
  // empty" fallback.
  const fetchMoreKontakte = async () => {
    try {
      const currentOffset = kontakte.length ?? 0;
      const { data: raw, total } = await apiFetch<any>("contact", {
        params: {
          limit: PAGE_SIZE,
          offset: currentOffset,
          search: debouncedSearch,
          filters: JSON.stringify(filters),
          scope,
          include: '',
        },
      });
      const data = Array.isArray(raw) ? raw : [];
      const mapped = Array.isArray(data) ? data.map(mapContactRow) : [];

      const newKontakte = dedupeById([...kontakte, ...mapped]);
      const newOffset = currentOffset + mapped.length;
      const newHasMore =
        total !== null ? newOffset < total : mapped.length > 0;

      setKontakte(newKontakte);
      setHasMore(newHasMore);
      setOffset(newOffset);
    } catch (err) {
      console.error("Fehler beim Nachladen der Kontakte", err);
      setHasMore(false);
    }
  };
  const [open, setOpen] = useState(false);
  const [chooseTypeOpen, setChooseTypeOpen] = useState(false);
  const [companyFormOpen, setCompanyFormOpen] = useState(false);
  const [privateFormOpen, setPrivateFormOpen] = useState(false);

  const updateFilter = (key: string, val: any) => {
    setFilters((prev) => {
      const next = { ...prev };
      if (val === undefined || val === "") {
        delete next[key];
      } else {
        next[key] = val;
      }
      return next;
    });
  };

  // Anzahl aktiver Filter (für das Badge am mobilen Filter-Button).
  const activeFilterCount = Object.keys(filters).length;
  const clearAllFilters = () => setFilters({});

  // Refs that mirror `selectedIds` and `filters` so the columns memo can
  // close over a stable reference. Without this, every checkbox toggle /
  // filter keystroke rebuilt the entire 10-column array, invalidated
  // `orderedColumns`, and forced `useTableFeatures` to re-render every row.
  // The actual rerender for the affected row still happens because
  // `kontakte` / `selectedIds` are passed to `useTableFeatures` directly.
  const selectedIdsRef = useRef(selectedIds);
  selectedIdsRef.current = selectedIds;
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  // BO teams + FO users for the "K-Verantwortlicher" filter. Loaded once; read
  // via refs in the columns memo so they don't invalidate it (like filtersRef).
  const [teamOptions, setTeamOptions] = useState<{ id: string; label: string }[]>([]);
  const [userOptions, setUserOptions] = useState<{ id: string; label: string }[]>([]);
  const teamOptionsRef = useRef(teamOptions);
  teamOptionsRef.current = teamOptions;
  const userOptionsRef = useRef(userOptions);
  userOptionsRef.current = userOptions;
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const teams = await fetchTeamsLite();
        if (alive && Array.isArray(teams)) {
          setTeamOptions(
            teams
              .map((t: any) => ({
                id: String(t.id),
                label: String(t.name ?? "").replace(/^Team\s+/i, ""),
              }))
              .filter((o: { id: string; label: string }) => o.id && o.label),
          );
        }
      } catch (e) {
        console.error("teams-lite laden fehlgeschlagen", e);
      }
      try {
        const users = await listAppUsers();
        if (alive) {
          setUserOptions(
            users
              // FO-Verantwortliche sind ausschließlich gfgl-User; admin /
              // mitarbeiter / service erscheinen nicht im FO-Picker.
              .filter((u) => (u.role ?? "").toLowerCase() === "gfgl")
              .map((u) => ({
                id: String(u.id),
                label:
                  [u.lastName, u.firstName].filter(Boolean).join(", ") ||
                  u.name ||
                  u.email ||
                  u.id,
              }))
              .filter((o) => o.id && o.label),
          );
        }
      } catch (e) {
        console.error("users laden fehlgeschlagen", e);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  // Spalten- Definition mit createTableColumn
  const columns: TableColumnDefinition<ContactDetailsFormValues>[] =
    React.useMemo(
      () => [
        createTableColumn<ContactDetailsFormValues>({
          columnId: "select",
          renderHeaderCell: () => <></>,
          renderCell: (item) => (
            <Checkbox
              checked={selectedIdsRef.current.has(item.kontaktId)}
              onClick={(e) => e.stopPropagation()}
              onChange={() => toggleSelect(item.kontaktId)}
            />
          )
        }),
        createTableColumn<ContactDetailsFormValues>({
          columnId: "Typ",
          renderHeaderCell: () => (
            <ColumnFilter
              label="Typ"
              type="dropdown"
              options={["Firma", "Privat"]}
              value={contactTypeLabel(filtersRef.current["Typ"])}
              onFilterChange={(v) => updateFilter("Typ", contactTypeValue(v))}
            />
          ),
          renderCell: (item) => <>{contactTypeLabel(item.typ)}</>,
        }),
        createTableColumn<ContactDetailsFormValues>({
          columnId: "Name",
          renderHeaderCell: () => (
            <ColumnFilter
              label="Name"
              value={filtersRef.current["Name"]}
              onFilterChange={(v) => updateFilter("Name", v)}
            />
          ),
          // Verstorbene tragen das Kreuz vor dem Namen — in der Tabelle wie in
          // der Akte (PO 13.09.2026: „überall"). Das Zeichen kommt aus der
          // Anzeige-Hilfe und NICHT aus `displayName`: Der Wert wird an anderer
          // Stelle gespeichert und gesucht, ein Kreuz darin wäre ein Datenfehler.
          renderCell: (item) => {
            const tot = istVerstorben(item);
            return (
              <div
                className={classes.cellEllipsis}
                title={tot ? `${verstorbenTitel(item)} — ${item.displayName}` : item.displayName}
              >
                {tot && (
                  <span aria-label="verstorben" style={{ opacity: 0.75, marginRight: 4 }}>
                    {VERSTORBEN_ZEICHEN}
                  </span>
                )}
                {item.displayName}
              </div>
            );
          },
        }),
        createTableColumn<ContactDetailsFormValues>({
          columnId: "Ort",
          renderHeaderCell: () => (
            <ColumnFilter
              label="Ort"
              value={filtersRef.current["Ort"]}
              onFilterChange={(v) => updateFilter("Ort", v)}
            />
          ),
          renderCell: (item) => <div className={classes.cellEllipsis} title={item.ort}>{item.ort}</div>,
        }),
        createTableColumn<ContactDetailsFormValues>({
          columnId: "Straße",
          renderHeaderCell: () => (
            <ColumnFilter
              label="Straße"
              value={filtersRef.current["Straße"]}
              onFilterChange={(v) => updateFilter("Straße", v)}
            />
          ),
          renderCell: (item) => <div className={classes.cellEllipsis} title={item.strasse}>{item.strasse}</div>,
        }),
        createTableColumn<ContactDetailsFormValues>({
          columnId: "Telefon",
          // User said "filter every column". I'll add boolean filter for "Has Phone/Email"
          renderHeaderCell: () => (
            <div style={{ display: 'flex', gap: 4 }}>
              <span>K-Möglichkeit</span>
              {/* Custom filter logic would fit here if needed, but sticky with simpler approach for action column */}
            </div>
          ),
          renderCell: (item) => (

            <div style={{ display: "flex", gap: "8px" }} >
              <Button
                icon={<Call20Regular />}
                appearance="subtle"
                onClick={(e) => {
                  e.stopPropagation();
                  setDialogState({ open: true, mode: "phone", contact: item });
                }}
              />
              <Button
                icon={<Mail20Regular />}
                appearance="subtle"
                onClick={(e) => {
                  e.stopPropagation();
                  setDialogState({ open: true, mode: "email", contact: item });
                }}
              />
            </div >
          ),

        }),
        createTableColumn<ContactDetailsFormValues>({
          columnId: "Kontaktstatus",
          renderHeaderCell: () => (
            <ColumnFilter
              label="K-Status"
              type="dropdown"
              options={["Interessent", "Kunde", "Sonstige", "Altkunde", "Unbekannt"]}
              value={filtersRef.current["Kontaktstatus"]}
              onFilterChange={(v) => updateFilter("Kontaktstatus", v)}
            />
          ),
          renderCell: (item) => {
            const rolesText = Array.isArray(item.roles)
              ? item.roles
                .map(
                  (r: any) =>
                    r?.roleDefinition?.contactRole ?? r?.contactRole ?? "",
                )
                .filter(Boolean)
                .join(", ")
              : "";
            // priorisiere kontaktStatus (aus status.status), dann roles, dann typ
            return (
              <div className={classes.cellEllipsis} title={
                item.kontaktStatus ?? rolesText ?? (item.typ !== undefined ? String(item.typ) : "")
              }>
                {item.kontaktStatus ??
                  rolesText ??
                  (item.typ !== undefined ? String(item.typ) : "")}
              </div>
            );
          },
        }),
        createTableColumn<ContactDetailsFormValues>({
          columnId: "Kontaktverantwortlicher",
          renderHeaderCell: () => (
            <ColumnFilter
              label="K-Verantwortlicher"
              type="boFo"
              boOptions={teamOptionsRef.current}
              foOptions={userOptionsRef.current}
              boSelected={filtersRef.current["boTeamIds"] ?? []}
              foSelected={filtersRef.current["foUserIds"] ?? []}
              onBoFoChange={({ boTeamIds, foUserIds }) => {
                updateFilter("boTeamIds", boTeamIds.length ? boTeamIds : undefined);
                updateFilter("foUserIds", foUserIds.length ? foUserIds : undefined);
              }}
              onFilterChange={() => {}}
            />
          ),
          renderCell: (item) => {
            // Cell text priority: Team Kürzel > BO Kürzel > FO Kürzel > "—".
            // Backend stores team names as "Team CLL" / "Team Co-RB" etc. — we
            // strip the leading "Team " so the cell shows just "CLL"/"Co-RB".
            // Full names stay in the tooltip on hover.
            const boKuerzel = item.boUser?.kuerzel ?? null;
            const foKuerzel = item.foUser?.kuerzel ?? null;
            const boFull = item.boUser?.displayName
              ? `${item.boUser.displayName}${item.boUser.kuerzel ? ` (${item.boUser.kuerzel})` : ""}`
              : null;
            const foFull = item.foUser?.displayName
              ? `${item.foUser.displayName}${item.foUser.kuerzel ? ` (${item.foUser.kuerzel})` : ""}`
              : null;
            const team = item.boTeam?.name ?? null;
            const teamShort = team ? team.replace(/^Team\s+/i, "") : null;
            const tooltipParts = [
              boFull && `BO: ${boFull}`,
              foFull && `FO: ${foFull}`,
              team && `Team: ${team}`,
            ].filter(Boolean);
            const tooltip = tooltipParts.length > 0 ? tooltipParts.join(" · ") : "Nicht zugewiesen";
            const cellText = teamShort ?? boKuerzel ?? foKuerzel ?? "—";
            return (
              <Tooltip content={{ children: tooltip }} relationship="label">
                <div className={classes.cellEllipsis}>{cellText}</div>
              </Tooltip>
            );
          },
        }),
        createTableColumn<ContactDetailsFormValues>({
          columnId: "Vorgange",
          renderHeaderCell: () => (
            <ColumnFilter
              label="Vorgänge"
              type="boolean"
              value={filtersRef.current["Vorgange"] === true || filtersRef.current["Vorgange"] === 'true'} // backend supports boolean
              onFilterChange={(v) => updateFilter("Vorgange", v)}
            />
          ),
          renderCell: (item) => {
            // The backend ships `openEventTypes` per row. The 'V' slot covers
            // open Vorgänge + linked Todos; the circle indicator keeps the
            // T/S/Z quadrants for when those sources are reintroduced.
            const types = getOpenEventTypes(item);
            if (types.length === 0) return <>-</>;
            return (
              <OpenEventsIndicator
                types={types}
                title="Offene Vorgänge"
                onClick={(e) => {
                  e.stopPropagation();
                  setVorgaengeDialog({ open: true, contact: item });
                }}
              />
            );
          },
        }),
      ],
      // `selectedIds` and `filters` are read via refs (above) so they don't
      // invalidate the columns memo on every toggle / keystroke. Re-render
      // of the affected cell still happens because the parent re-renders;
      // the win is that `useTableFeatures` sees a stable column array.
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [classes],
    );

  const orderedColumns = React.useMemo(() => {
    const columnMap = new Map(
      columns.map((column) => [String(column.columnId), column] as const),
    );

    const selectColumn = columnMap.get("select");
    const reordered = columnOrder
      .map((columnId) => columnMap.get(columnId))
      .filter((column): column is TableColumnDefinition<ContactDetailsFormValues> => Boolean(column));

    return selectColumn ? [selectColumn, ...reordered] : reordered;
  }, [columns, columnOrder]);

  // ColumnSizingOptions definieren
  const columnSizingOptions: TableColumnSizingOptions = React.useMemo(
    () => ({
      select: { defaultWidth: 48, minWidth: 48 },
      Typ: { defaultWidth: 80, minWidth: 40 },
      Name: { defaultWidth: 200, minWidth: 100 },
      Ort: { defaultWidth: 80, minWidth: 80 },
      Straße: { defaultWidth: 150, minWidth: 100 },
      Telefon: { defaultWidth: 150, minWidth: 100 },
      Kontaktstatus: { defaultWidth: 150, minWidth: 100 },
      Kontaktverantwortlicher: { defaultWidth: 180, minWidth: 100 },
      ["Vorgnge"]: { defaultWidth: 150, minWidth: 100 },
    }),
    [],
  );

  // Table-Features und rows IMMER mit aktuellem State berechnen
  const tableFeatures = useTableFeatures({ columns: orderedColumns, items: kontakte }, [
    useTableColumnSizing_unstable({ columnSizingOptions }),
  ]);

  const rows = tableFeatures.getRows();

  const handleSearchChange = (value: string) => {
    setSearch(value);
  };

  const handleNew = () => {
    //setModalOpen(true);
    setChooseTypeOpen(true); // Modal öffnen
  };

  const handleSaveContact = async (data: any) => {
    // Backend-POST hier
    // Beispiel:
    await fetch(apiUrl("contact"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });
    setModalOpen(false); // Modal schließen
    // Optional: Kontakte neu laden
  };

  // Auswahl-Handler
  const toggleSelect = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  function toggleContactForm(value: boolean) {
    setChooseTypeOpen(value);
  }

  // Navigation zu Edit
  const handleEdit = (item: ContactDetailsFormValues) => {
    navigate(`/kontakte/edit/${item.kontaktId}`, { state: { k: item } });
  };

  // Bearbeiten aus der Commandbar: aktiv nur bei genau 1 Auswahl → Edit-Seite
  // des gewählten Kontakts (state-Vorbefüllung wie beim Zeilen-Edit).
  const handleEditSelected = () => {
    if (selectedIds.size !== 1) return;
    const [id] = Array.from(selectedIds);
    const item = kontakte.find((k) => k.kontaktId === id);
    if (!item) return;
    handleEdit(item);
  };

  // Zusammenführen: aktiv ab 2 Auswahl → Merge-Prüfungsseite mit der Auswahl
  // als Cluster (ContactMergePage parst ?clusterIds=…).
  const handleMerge = () => {
    if (selectedIds.size < 2) return;
    const ids = Array.from(selectedIds).join(",");
    navigate(`/contacts/merge?clusterIds=${ids}`);
  };

  const canMerge = selectedIds.size > 1;
  const canEdit = selectedIds.size === 1;

  // Löschen
  const handleDelete = (id: number) => {
    setKontakte((prev) => prev.filter((k) => k.kontaktId !== id));
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };

  // One bulk-DELETE round-trip via BE 4's /api/contact/bulk?ids=… endpoint.
  // The response carries `{ deleted, skipped }`; we update local state to
  // remove only the actually-deleted ids and surface skipped ones via
  // eigenem Hinweis-Popup (hinweisen).
  const handleDeleteSelected = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    if (ids.length > 200) {
      void hinweisen({
        titel: "Zu viele Kontakte ausgewählt",
        text: `Es lassen sich höchstens 200 Kontakte auf einmal löschen – ausgewählt sind ${ids.length}. Bitte die Auswahl verkleinern.`,
      });
      return;
    }
    try {
      const { data } = await apiFetch<{
        deleted: number[];
        skipped: Array<{ id: number; reason: 'not_found' | 'forbidden' }>;
      }>("contact/bulk", {
        method: "DELETE",
        params: { ids: ids.join(",") },
      });
      const deletedSet = new Set(data.deleted);
      setKontakte((prev) => prev.filter((k) => !deletedSet.has(k.kontaktId)));
      setSelectedIds((prev) => {
        const next = new Set(prev);
        for (const id of data.deleted) next.delete(id);
        return next;
      });
      if (data.skipped && data.skipped.length > 0) {
        const counts = data.skipped.reduce(
          (acc, s) => ({ ...acc, [s.reason]: (acc[s.reason] ?? 0) + 1 }),
          {} as Record<string, number>,
        );
        const parts = [
          `${data.deleted.length} gelöscht`,
          counts.forbidden ? `${counts.forbidden} ohne Berechtigung` : null,
          counts.not_found ? `${counts.not_found} nicht gefunden` : null,
        ].filter(Boolean);
        void hinweisen({ titel: "Nicht alle Kontakte gelöscht", text: parts.join(", ") + "." });
      }
    } catch (err) {
      console.error("Bulk delete failed", err);
      void hinweisen({ titel: "Löschen fehlgeschlagen", text: "Die Kontakte konnten nicht gelöscht werden. Bitte später noch einmal versuchen.", ton: "danger" });
    }
  };

  // Server-computed open-work indicator. Only 'V' is emitted today (open
  // Vorgänge + linked Todos); the type stays the full union for future T/S/Z.
  const getOpenEventTypes = (item: ContactDetailsFormValues): EventTypeKey[] =>
    (item.openEventTypes ?? []) as EventTypeKey[];

  const handleColumnDragStart = (
    event: React.DragEvent<HTMLElement>,
    columnId: ReorderableColumnId,
  ) => {
    const target = event.target as HTMLElement | null;
    if (target?.closest("input, button, select, textarea")) {
      event.preventDefault();
      return;
    }
    setDraggedColumnId(columnId);
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("text/plain", columnId);
  };

  const handleColumnDragOver = (
    event: React.DragEvent<HTMLElement>,
    columnId: ReorderableColumnId,
  ) => {
    if (!draggedColumnId || draggedColumnId === columnId) return;
    event.preventDefault();

    const rect = event.currentTarget.getBoundingClientRect();
    const position = event.clientX < rect.left + rect.width / 2 ? "before" : "after";
    setDropTarget({ columnId, position });
  };

  const handleColumnDrop = (
    event: React.DragEvent<HTMLElement>,
    columnId: ReorderableColumnId,
  ) => {
    event.preventDefault();
    if (!draggedColumnId || draggedColumnId === columnId) {
      setDraggedColumnId(null);
      setDropTarget(null);
      return;
    }

    const rect = event.currentTarget.getBoundingClientRect();
    const position = event.clientX < rect.left + rect.width / 2 ? "before" : "after";

    setColumnOrder((prev) => {
      const next = prev.filter((id) => id !== draggedColumnId);
      const targetIndex = next.indexOf(columnId);
      if (targetIndex < 0) return prev;
      const insertIndex = position === "before" ? targetIndex : targetIndex + 1;
      next.splice(insertIndex, 0, draggedColumnId);
      return next;
    });

    setDraggedColumnId(null);
    setDropTarget(null);
  };

  const handleColumnDragEnd = () => {
    setDraggedColumnId(null);
    setDropTarget(null);
  };

  return (
    <div style={{ fontSize: "14px", width: "100%", ...(isMobileView ? {} : { overflowX: "auto" }) }}>
      <div
        style={{
          display: "flex",
          flexDirection: isMobileView ? "column" : "row",
          alignItems: isMobileView ? "stretch" : "center",
          gap: isMobileView ? 8 : 12,
          width: "100%",
        }}
      >
        {viewMode === "contacts" && (
          <>
            <div style={{ flex: isMobileView ? "0 0 auto" : 1, minWidth: 0, width: isMobileView ? "100%" : undefined }}>
              <CommandBar
                header="Suche"
                compact={isMobileView}
                onSearchChange={handleSearchChange}
                onNewClick={handleNew}
                onMergeClick={handleMerge}
                canMerge={canMerge}
                onEditClick={handleEditSelected}
                canEdit={canEdit}
                onDeleteSelected={handleDeleteSelected} // <--- hinzufügen!
                canDelete={canDelete}
              />
            </div>
            {/* Auf dem Phone gibt es keine Tabellen-Spaltenköpfe → Filter über
                einen Button + Dialog zugänglich machen. */}
            {isPhone && (
              <Button
                appearance="outline"
                icon={<Filter20Regular />}
                onClick={() => setFilterSheetOpen(true)}
                style={{ width: "100%", justifyContent: "center" }}
              >
                {activeFilterCount > 0 ? `Filter (${activeFilterCount})` : "Filter"}
              </Button>
            )}
            <button
              type="button"
              onClick={() => navigate("/contacts/duplicates")}
              title="Duplikatenprüfung"
              style={{
                appearance: "none",
                cursor: "pointer",
                backgroundColor: "#fff",
                color: "#4d2051",
                border: "1px solid #ECE7F0",
                borderRadius: 8,
                padding: "8px 14px",
                fontSize: 14,
                fontWeight: 600,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 6,
                whiteSpace: "nowrap",
              }}
            >
              Duplikate prüfen
            </button>
            {canDelete && (
              <button
                type="button"
                onClick={() => navigate("/kontakte/papierkorb")}
                title="Papierkorb (gelöschte Kontakte)"
                style={{
                  appearance: "none",
                  cursor: "pointer",
                  backgroundColor: "#fff",
                  color: "#4d2051",
                  border: "1px solid #ECE7F0",
                  borderRadius: 8,
                  padding: "8px 14px",
                  fontSize: 14,
                  fontWeight: 600,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 6,
                  whiteSpace: "nowrap",
                }}
              >
                Papierkorb
              </button>
            )}
          </>
        )}
        <div
          style={{
            marginLeft: isMobileView ? 0 : "auto",
            maxWidth: "100%",
            // Auf dem Phone darf die Scope-Auswahl horizontal scrollen, statt
            // unschön umzubrechen — so bleiben "Alle/Team/Persönlich" erreichbar.
            overflowX: isMobileView ? "auto" : "visible",
          }}
        >
          <ContactScopeToggle
            scope={scope}
            viewMode={viewMode}
            onScopeChange={setScope}
            onViewModeChange={setViewMode}
          />
        </div>
      </div>

      {/* Tablet-Hochformat: Hinweis aufs Drehen — ein echtes Erzwingen der
          Rotation ist im Browser (insb. iPad-Safari) nicht möglich. */}
      {isTablet && isPortrait && !rotateHintDismissed && viewMode === "contacts" && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            margin: "8px 0",
            padding: "10px 14px",
            backgroundColor: "#f8eef5",
            border: "1px solid #e2bcd7",
            borderRadius: 10,
            color: "#3a0c31",
            fontSize: 14,
          }}
        >
          <span style={{ flex: 1 }}>
            Für die beste Übersicht der Kontakttabelle das Gerät ins Querformat drehen.
          </span>
          <Button
            appearance="subtle"
            icon={<Dismiss20Regular />}
            aria-label="Hinweis schließen"
            onClick={() => setRotateHintDismissed(true)}
          />
        </div>
      )}

      <ContactModesDialog
        open={dialogState.open}
        contact={dialogState.contact}
        mode={dialogState.mode}
        onOpenChange={(open) => setDialogState((prev) => ({ ...prev, open }))}
      />

      <OffeneVorgaengeDialog
        open={vorgaengeDialog.open}
        onOpenChange={(open) => setVorgaengeDialog((prev) => ({ ...prev, open }))}
        contactId={vorgaengeDialog.contact?.kontaktId ?? null}
        contactName={vorgaengeDialog.contact?.displayName ?? ""}
      />

      {/* Mobiles Filter-Sheet — bündelt dieselben Filter wie die Tabellen-
          Spaltenköpfe (ColumnFilter), damit auf dem Phone vollwertig gefiltert
          werden kann. */}
      <Dialog
        open={filterSheetOpen}
        onOpenChange={(_e, d) => setFilterSheetOpen(d.open)}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Kontakte filtern</DialogTitle>
            <DialogContent>
              <div className={classes.mobileFilterList} style={{ display: "flex", flexDirection: "column" }}>
                {[
                  <ColumnFilter
                    key="Typ"
                    label="Typ"
                    type="dropdown"
                    options={["Firma", "Privat"]}
                    value={contactTypeLabel(filters["Typ"])}
                    onFilterChange={(v) => updateFilter("Typ", contactTypeValue(v))}
                  />,
                  <ColumnFilter
                    key="Name"
                    label="Name"
                    value={filters["Name"]}
                    onFilterChange={(v) => updateFilter("Name", v)}
                  />,
                  <ColumnFilter
                    key="Ort"
                    label="Ort"
                    value={filters["Ort"]}
                    onFilterChange={(v) => updateFilter("Ort", v)}
                  />,
                  <ColumnFilter
                    key="Straße"
                    label="Straße"
                    value={filters["Straße"]}
                    onFilterChange={(v) => updateFilter("Straße", v)}
                  />,
                  <ColumnFilter
                    key="Kontaktstatus"
                    label="K-Status"
                    type="dropdown"
                    options={["Interessent", "Kunde", "Sonstige", "Altkunde", "Unbekannt"]}
                    value={filters["Kontaktstatus"]}
                    onFilterChange={(v) => updateFilter("Kontaktstatus", v)}
                  />,
                  <ColumnFilter
                    key="Kontaktverantwortlicher"
                    label="K-Verantwortlicher"
                    type="boFo"
                    boOptions={teamOptions}
                    foOptions={userOptions}
                    boSelected={filters["boTeamIds"] ?? []}
                    foSelected={filters["foUserIds"] ?? []}
                    onBoFoChange={({ boTeamIds, foUserIds }) => {
                      updateFilter("boTeamIds", boTeamIds.length ? boTeamIds : undefined);
                      updateFilter("foUserIds", foUserIds.length ? foUserIds : undefined);
                    }}
                    onFilterChange={() => {}}
                  />,
                  <ColumnFilter
                    key="Vorgange"
                    label="Offene Vorgänge"
                    type="boolean"
                    value={filters["Vorgange"] === true || filters["Vorgange"] === "true"}
                    onFilterChange={(v) => updateFilter("Vorgange", v)}
                  />,
                ].map((node, idx) => (
                  <div
                    key={(node as React.ReactElement).key ?? idx}
                    style={{
                      padding: "12px 2px",
                      borderTop: idx === 0 ? "none" : "1px solid #ece6ec",
                    }}
                  >
                    {node}
                  </div>
                ))}
              </div>
            </DialogContent>
            <DialogActions>
              <Button
                appearance="secondary"
                disabled={activeFilterCount === 0}
                onClick={clearAllFilters}
              >
                Zurücksetzen
              </Button>
              <Button appearance="primary" onClick={() => setFilterSheetOpen(false)}>
                Fertig
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      <ContactMultiStepFormCompany open={open} onClose={() => setOpen(false)} />

      <CreateContact
        open={chooseTypeOpen}
        toggleContactForm={toggleContactForm}
      />
      {/* Modal für neuen Kontakt */}
      {/* <ContactMultiStepForm
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSave={handleSaveContact}
      /> */}
      {viewMode === "kundenverbindungen" ? (
        <div style={{ height: "calc(100vh - 150px)", overflowY: "auto" }}>
          <KundenverbindungList />
        </div>
      ) : (
      <div
        id="scrollableDiv"
        style={{ height: "calc(100vh - 150px)", overflowY: "auto" }}
      >
        <InfiniteScroll
          dataLength={kontakte.length}
          next={fetchMoreKontakte}
          hasMore={hasMore}
          loader={
            <div style={{ textAlign: "center" }}>Lade weitere Kontakte...</div>
          }
          scrollableTarget="scrollableDiv"
          style={{ overflow: "visible" }}
        >
          {isMobileView ? (
            <div className={classes.mobileCards}>
              {kontakte.map((item) => {
                const isInteressent =
                  item.status?.status === "Interessent" ||
                  item.kontaktStatus === "Interessent";
                const openEventTypes = getOpenEventTypes(item);

                return (
                  <div
                    key={item.kontaktId}
                    className={classes.contactCard}
                    onClick={() => handleEdit(item)}
                  >
                    <div className={classes.cardTopBand} />
                    <div className={classes.cardContent}>
                      <div className={classes.cardHeader}>
                        <div className={classes.cardIdentity}>
                          <div
                            className={classes.cardAvatar}
                            style={{
                              backgroundColor:
                                item.typ === "Firma" ? "#6A2C91" : "#2f6fed",
                            }}
                          >
                            {item.typ === "Firma" ? (
                              <Building32Filled style={{ fontSize: 20 }} />
                            ) : (
                              <Person32Filled style={{ fontSize: 20 }} />
                            )}
                          </div>
                          <div className={classes.cardHeaderMain}>
                            <div className={classes.cardTypeBadge}>{contactTypeLabel(item.typ) || "Kontakt"}</div>
                            <div className={classes.cardTitle}>{item.displayName || "-"}</div>
                            <div className={classes.cardSubTitle}>{item.kontaktStatus || "Status unbekannt"}</div>
                          </div>
                        </div>
                        <Checkbox
                          checked={selectedIdsRef.current.has(item.kontaktId)}
                          onClick={(e) => e.stopPropagation()}
                          onChange={() => toggleSelect(item.kontaktId)}
                        />
                      </div>

                      <div className={classes.cardBody}>
                        <div className={classes.cardRow}>
                          <span className={classes.cardLabel}>Ort</span>
                          <span className={classes.cardValue}>{item.ort || "-"}</span>
                        </div>
                        <div className={classes.cardRow}>
                          <span className={classes.cardLabel}>Straße</span>
                          <span className={classes.cardValue}>{item.strasse || "-"}</span>
                        </div>
                        <div className={classes.cardRow}>
                          <span className={classes.cardLabel}>Verantwortlich</span>
                          <span className={classes.cardValue}>{item.kundenberater || "-"}</span>
                        </div>
                        <div className={classes.cardRow}>
                          <span className={classes.cardLabel}>Relevanz</span>
                          <span className={classes.cardValue}>
                            {isInteressent && item.phase ? (
                              <span className={classes.statusChip}>{item.phase}</span>
                            ) : (
                              <RatingDisplay
                                value={Math.floor((item.relevanz || 0) * 2) / 2}
                                max={5}
                                size="small"
                                color="marigold"
                              />
                            )}
                          </span>
                        </div>
                      </div>

                      <div className={classes.cardActions} onClick={(e) => e.stopPropagation()}>
                        <div style={{ display: "flex", gap: 6 }}>
                          <Button
                            icon={<Call20Regular />}
                            appearance="subtle"
                            onClick={() => setDialogState({ open: true, mode: "phone", contact: item })}
                          />
                          <Button
                            icon={<Mail20Regular />}
                            appearance="subtle"
                            onClick={() => setDialogState({ open: true, mode: "email", contact: item })}
                          />
                        </div>
                        {openEventTypes.length > 0 ? (
                          <OpenEventsIndicator
                            types={openEventTypes}
                            title="Offene Vorgänge"
                            onClick={(e) => {
                              e.stopPropagation();
                              setVorgaengeDialog({ open: true, contact: item });
                            }}
                          />
                        ) : (
                          <span className={classes.cardLabel}>Keine offenen Vorgänge</span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <Table
              ref={tableFeatures.tableRef}
              aria-label="Risiken-Tabelle"
              {...tableFeatures.columnSizing_unstable.getTableProps()}
            >
              <TableHeader>
                <TableRow>
                  {orderedColumns.map((col) => {
                    const columnId = String(col.columnId);
                    const isReorderable = columnId !== "select";
                    // Fluent UI atomic classes (`___xxxxx`) MUST be combined with
                    // `mergeClasses` — joining with a plain space corrupts the
                    // class identifier and triggers a runtime warning.
                    const headerClassName = mergeClasses(
                      classes.stickyHeader,
                      isReorderable && classes.draggableHeader,
                      draggedColumnId === columnId && classes.draggingHeader,
                      dropTarget?.columnId === columnId && dropTarget.position === "before"
                        && classes.dropIndicatorBefore,
                      dropTarget?.columnId === columnId && dropTarget.position === "after"
                        && classes.dropIndicatorAfter,
                    );

                    return (
                    <TableHeaderCell
                      key={col.columnId}
                      className={headerClassName}
                      draggable={isReorderable}
                      onDragStart={
                        isReorderable
                          ? (event) =>
                              handleColumnDragStart(event, columnId as ReorderableColumnId)
                          : undefined
                      }
                      onDragOver={
                        isReorderable
                          ? (event) =>
                              handleColumnDragOver(event, columnId as ReorderableColumnId)
                          : undefined
                      }
                      onDrop={
                        isReorderable
                          ? (event) =>
                              handleColumnDrop(event, columnId as ReorderableColumnId)
                          : undefined
                      }
                      onDragEnd={isReorderable ? handleColumnDragEnd : undefined}
                      {...tableFeatures.columnSizing_unstable.getTableHeaderCellProps(
                        col.columnId,
                      )}
                    >
                      {col.columnId === "select" ? null : col.renderHeaderCell()}
                    </TableHeaderCell>
                    );
                  })}
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map(({ item }, i) => (
                  <TableRow
                    key={item.kontaktId}
                    className={i % 2 === 1 ? classes.stripedRow : undefined}
                    onClick={() => handleEdit(item)}
                  >
                    {orderedColumns.map((col) => (
                      <TableCell
                        key={col.columnId}
                        {...tableFeatures.columnSizing_unstable.getTableCellProps(col.columnId)}
                      >
                        {col.renderCell(item)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </InfiniteScroll>
      </div>
      )}
    </div>
  );
};

export default ContactTable;
